import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../partage/api/client';
import type { DossierVu, JourDuCalendrier } from '../partage/api/types';
import { FeuilleDuBas } from '../partage/FeuilleDuBas';
import { HautDuPouce } from '../partage/HautDuPouce';
import { LigneQuiOuvre } from '../partage/PiecesDuCarnet';
import {
  AGENDA,
  CA_N_A_PAS_MARCHE,
  MOTS_AGENDA_AU_POUCE as MOTS,
  ON_RECOMMENCE,
  REGLAGE_PROJET,
  RIEN_DE_PREVU_AUJOURDHUI,
  RIEN_DE_PREVU_CE_JOUR,
} from '../partage/vocabulaire';
import { FichePosee, useAllerALaFiche } from '../fiche';
import { cles } from '../partage/relectures';
import { useRepondreAuMonde } from '../partage/useArbre';
import { cartesDuJour, jourDuProjet, SIGNES, toucherDUneCarte, type CarteDuJour } from './cartesDeLaSemaine';
import { ChangerMaJournee, ouvreLaQuestion, PlanifierUnJour } from '../maintenant';
import { apercuDuJour, barresDuJour, rubriquesDuJour, toucherDansLeMois } from './leMoisEtLeJour';
import {
  decaler,
  dureeLisible,
  heureLisible,
  jourEnToutesLettres,
  moisEnLettres,
  nomCourtDuJour,
  quantiemeEtMoisAbrege,
} from '../partage/datesEnMots';
import {
  aujourdhuiDans,
  bullesAExporter,
  gesteDuJour,
  lundiDe,
  moisDeLaSemaine,
  semaineDans,
  type GesteDuJour,
} from './semaineAuPouce';
import { teinteDuProjet } from '../apparence';
import './AgendaAuPouce.css';

/**
 * L'Agenda au téléphone (ticket 10 de telephone, `design/telephone.html`,
 * « Agenda ») : le Calendrier seul, jamais la Chronologie.
 *
 * - **Mois**, l'ouverture (chantier `telephone-ecrans`, 02, maquette `agenda()`, `casesDuMois()`,
 *   `leJour()`) : le mois en grand, « Octobre 2026 ‹ › » à la main ; chaque case son numéro à la
 *   main et ses choses nommées, teintées par leur sorte (rendez-vous, réponse attendue, échéance,
 *   ⇄ chevauchement, ta journée), trois au plus puis « +N » (`leMoisEtLeJour.ts`) ; aujourd'hui
 *   souligné, le jour touché entouré ; dessous, le jour touché (aujourd'hui par défaut), ses
 *   lignes dans un paquet ; « Ouvrir » l'affiche en entier.
 * - **Semaine**, en second : un jour par ligne, chaque chose dans une carte au ton clair de la
 *   couleur de son projet, son signe en tête, sans légende (chantier `telephone-journee-agenda`,
 *   ticket 05, `cartesDeLaSemaine.ts`) ; une réponse pas venue y a « Relancer » et « C'est
 *   arrivé » ; « Exporter vers mon agenda » en bas.
 * - **Un jour ouvert** : « Imposé », « Ta journée » (« Planifier ce jour » ou « Changer ce
 *   jour »), « Ce qui doit arriver » ; chaque ligne ouvre sa fiche ; rien pour un jour passé.
 * - « Tous les projets ▾ » au-dessus de la semaine et du mois les filtre par projet.
 *
 * Tout vit dans l'adresse (`?vue=semaine&semaine=<lundi>`, `?vue=mois&mois=<aaaa-mm>`,
 * `&projet=<id>`, `&touche=<jour touché du mois>`, `&jour=<jour ouvert>`, `&preparer=oui` quand
 * la question ou la journée à changer est ouverte) : un rechargement garde
 * l'écran, et le chemin reste `/agenda`. Les jours viennent du serveur (`GET /api/calendrier`) ;
 * la semaine se lit dans la grille du mois de son lundi (`semaineAuPouce.ts`).
 *
 * - **Couché** (`couche`, chantier telephone-couche-suite, 04, maquette `cAgenda()`) : le mois
 *   seul, en titre avec « ‹ › » ; à gauche ses cases, les mêmes que droites ; à droite le jour
 *   touché (aujourd'hui à l'ouverture) et la fiche du téléphone de son premier rendez-vous, posée
 *   (`FichePosee`). Un jour ouvert reste celui de l'écran droit.
 */
export function AgendaAuPouce({ couche = false }: { couche?: boolean } = {}) {
  const [parametres, setParametres] = useSearchParams();
  // Le mois en grand à l'ouverture (chantier telephone-ecrans, 02, décision 2 d'Igor) ; Semaine en second.
  const vue = parametres.get('vue') === 'semaine' ? 'semaine' : 'mois';
  const semaine = parametres.get('semaine');
  const mois = parametres.get('mois');
  const jour = parametres.get('jour');
  const preparer = parametres.get('preparer') === 'oui';
  const projetDemande = parametres.get('projet');
  const touche = parametres.get('touche');

  // « Tous les projets ▾ » : les projets de « Projets ». Un projet qui n'existe plus ne filtre rien.
  const projets = useQuery({ queryKey: cles.projets, queryFn: api.projets });
  const dossiers = projets.data?.dossiers ?? [];
  const projetVu = dossiers.find((d) => d.id === projetDemande) ?? null;
  const projet = projets.data && !projetVu ? null : projetDemande;

  // Ce que le panneau dit en se refermant (« Ta journée du … est prête »), quelques secondes.
  const [annonce, setAnnonce] = useState<string | null>(null);
  useEffect(() => {
    if (!annonce) return;
    const minuterie = setTimeout(() => setAnnonce(null), 4000);
    return () => clearTimeout(minuterie);
  }, [annonce]);

  /** Change ce qu'on regarde, sans empiler une page par geste. */
  function aller(suite: {
    vue?: 'semaine' | 'mois';
    semaine?: string | null;
    mois?: string | null;
    jour?: string | null;
    preparer?: boolean;
    projet?: string | null;
    touche?: string | null;
  }) {
    const p = new URLSearchParams();
    const valeurs = {
      vue: suite.vue ?? vue,
      semaine: suite.semaine === undefined ? semaine : suite.semaine,
      mois: suite.mois === undefined ? mois : suite.mois,
      jour: suite.jour === undefined ? jour : suite.jour,
      projet: suite.projet === undefined ? projet : suite.projet,
      // Le jour touché du mois ne survit ni à un autre mois ni à la semaine.
      touche: suite.touche !== undefined ? suite.touche : suite.mois !== undefined || suite.vue !== undefined ? null : touche,
    };
    p.set('vue', valeurs.vue);
    if (valeurs.semaine) p.set('semaine', valeurs.semaine);
    if (valeurs.mois) p.set('mois', valeurs.mois);
    if (valeurs.projet) p.set('projet', valeurs.projet);
    if (valeurs.touche && valeurs.vue === 'mois') p.set('touche', valeurs.touche);
    if (valeurs.jour) p.set('jour', valeurs.jour);
    if (valeurs.jour && (suite.preparer ?? (suite.jour === undefined && preparer))) p.set('preparer', 'oui');
    setParametres(p, { replace: true });
    // Toucher un jour du mois le montre dessous, sur place.
    if (suite.touche === undefined) window.scrollTo({ top: 0 });
  }

  return (
    <div className={`agenda-pouce${couche && !jour ? ' agenda-pouce--couche' : ''}`}>
      {jour ? (
        <JourOuvert
          jour={jour}
          retour={vue}
          preparer={preparer}
          onRetour={() => aller({ jour: null })}
          onGeste={() => aller({ preparer: true })}
          onFermer={(message) => {
            aller({ preparer: false });
            if (message) setAnnonce(message);
          }}
          onAnnonce={setAnnonce}
        />
      ) : couche ? (
        <Mois
          couche
          mois={mois}
          projet={projet}
          touche={touche}
          onMois={(m) => aller({ mois: m })}
          onToucher={(j) => aller({ touche: j })}
          onJour={(j) => aller({ jour: j })}
        />
      ) : (
        <>
          <HautDuPouce titre={MOTS.titre} />
          <div className="agenda-pouce-bascule" role="tablist" aria-label={MOTS.bascule}>
            <button
              type="button"
              role="tab"
              aria-selected={vue === 'mois'}
              className={vue === 'mois' ? 'est-choisi' : ''}
              onClick={() => aller({ vue: 'mois' })}
            >
              {MOTS.mois}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={vue === 'semaine'}
              className={vue === 'semaine' ? 'est-choisi' : ''}
              onClick={() => aller({ vue: 'semaine' })}
            >
              {MOTS.semaine}
            </button>
          </div>
          <ChoixDuProjet projet={projetVu} dossiers={dossiers} onProjet={(id) => aller({ projet: id })} />
          {vue === 'semaine' ? (
            <Semaine
              lundi={semaine}
              projet={projet}
              onSemaine={(l) => aller({ semaine: l })}
              onJour={(j) => aller({ jour: j })}
              onAnnonce={setAnnonce}
            />
          ) : (
            <Mois
              mois={mois}
              projet={projet}
              touche={touche}
              onMois={(m) => aller({ mois: m })}
              onToucher={(j) => aller({ touche: j })}
              onJour={(j) => aller({ jour: j })}
            />
          )}
        </>
      )}
      <p className={`agenda-pouce-annonce${annonce ? ' est-visible' : ''}`} role="status" aria-live="polite">
        {annonce}
      </p>
    </div>
  );
}

const teinte = (couleur: string | null | undefined): CSSProperties => ({
  ['--teinte' as string]: teinteDuProjet(couleur),
});

/**
 * « Projet  Tous les projets › » (cadrage de `telephone-journee-agenda`, décision 10 ; une ligne
 * du carnet depuis le chantier telephone-habit, 03) : la liste des projets dans la feuille du bas,
 * comme dans « Je choisis » ; la ligne dit le projet choisi, « Tous les projets » revient à tous.
 */
function ChoixDuProjet({
  projet,
  dossiers,
  onProjet,
}: {
  projet: DossierVu | null;
  dossiers: DossierVu[];
  onProjet: (id: string | null) => void;
}) {
  const [ouverte, setOuverte] = useState(false);
  const choisir = (id: string | null) => {
    setOuverte(false);
    onProjet(id);
  };
  const ligne = (cle: string, choisie: boolean, titre: string, onChoisir: () => void, couleur?: string | null) => (
    <button
      key={cle}
      type="button"
      role="radio"
      aria-checked={choisie}
      className={`agenda-pouce-feuille-ligne${choisie ? ' est-choisie' : ''}`}
      data-projet={cle}
      onClick={onChoisir}
    >
      {couleur !== undefined && <span className="agenda-pouce-pastille" style={teinte(couleur)} aria-hidden="true" />}
      <span className="agenda-pouce-feuille-mot">{titre}</span>
      <span className="agenda-pouce-feuille-coche" aria-hidden="true">
        {choisie ? '✓' : ''}
      </span>
    </button>
  );
  return (
    <div className="agenda-pouce-projet">
      {/* Une ligne qui ouvre sa liste (chantier telephone-habit, 03) : « Tous les projets » y revient à tous. */}
      <LigneQuiOuvre
        titre={REGLAGE_PROJET}
        valeur={projet ? projet.titre : MOTS.tousLesProjets}
        ouverte={ouverte}
        onOuvrir={() => setOuverte(true)}
      />
      {ouverte && (
        <FeuilleDuBas titre={MOTS.quelProjet} onFermer={() => setOuverte(false)}>
          <div className="agenda-pouce-feuille" role="radiogroup" aria-label={MOTS.quelProjet}>
            {ligne('tous', projet === null, MOTS.tousLesProjets, () => choisir(null))}
            {dossiers.map((d) => ligne(d.id, projet?.id === d.id, d.titre, () => choisir(d.id), d.couleur))}
          </div>
        </FeuilleDuBas>
      )}
    </div>
  );
}

function Periode({
  titre,
  avant,
  apres,
  onAvant,
  onApres,
  aLaMain = false,
}: {
  titre: string;
  avant: string;
  apres: string;
  onAvant?: () => void;
  onApres?: () => void;
  /** Le mois : « Octobre 2026 ‹ › » écrit à la main, les flèches en petit après (`.titre-s`). */
  aLaMain?: boolean;
}) {
  if (aLaMain) {
    return (
      <div className="agenda-pouce-periode agenda-pouce-periode--main">
        <b>{titre}</b>
        <Fleches avant={avant} apres={apres} onAvant={onAvant} onApres={onApres} />
      </div>
    );
  }
  return (
    <div className="agenda-pouce-periode">
      <button type="button" aria-label={avant} disabled={!onAvant} onClick={onAvant}>
        ‹
      </button>
      <b>{titre}</b>
      <button type="button" aria-label={apres} disabled={!onApres} onClick={onApres}>
        ›
      </button>
    </div>
  );
}

/** « ‹ › » en petit après le mois écrit à la main (`.titre-s small`), droit comme couché. */
function Fleches({
  avant,
  apres,
  onAvant,
  onApres,
}: {
  avant: string;
  apres: string;
  onAvant?: () => void;
  onApres?: () => void;
}) {
  return (
    <small className="agenda-pouce-fleches">
      <button type="button" aria-label={avant} disabled={!onAvant} onClick={onAvant}>
        ‹
      </button>
      <button type="button" aria-label={apres} disabled={!onApres} onClick={onApres}>
        ›
      </button>
    </small>
  );
}

function Semaine({
  lundi,
  projet,
  onSemaine,
  onJour,
  onAnnonce,
}: {
  lundi: string | null;
  projet: string | null;
  onSemaine: (lundi: string) => void;
  onJour: (jour: string) => void;
  onAnnonce: (message: string) => void;
}) {
  // Sans semaine dans l'adresse, le mois d'aujourd'hui, qui porte toujours la semaine d'aujourd'hui.
  const leMois = lundi ? moisDeLaSemaine(lundi) : null;
  const calendrier = useQuery({ queryKey: cles.calendrier(leMois), queryFn: () => api.calendrier(leMois) });
  const jours = calendrier.data?.jours ?? [];
  const aujourdhui = aujourdhuiDans(jours);
  const leLundi = lundi ?? (aujourdhui ? lundiDe(aujourdhui) : jours[7] ? lundiDe(jours[7].jour) : null);
  const semaine = leLundi ? semaineDans(jours, leLundi)?.map((j) => jourDuProjet(j, projet)) ?? null : null;
  // L'export suit ce que la semaine montre : un projet choisi n'exporte que le sien.
  const exportees = semaine ? bullesAExporter(semaine) : [];

  // La réponse pas venue touchée : ses deux gestes s'ouvrent sous sa carte, une seule à la fois.
  const [ouverte, setOuverte] = useState<string | null>(null);
  const naviguer = useNavigate();
  const ouvrirLaFiche = useAllerALaFiche();
  const repondre = useRepondreAuMonde();
  const repondreA = (c: CarteDuJour, jour: string, geste: 'relancer' | 'arrive') => {
    if (!c.bulleId) return;
    // Les mêmes effets que « À vérifier » de « Maintenant » : relancer repart d'aujourd'hui,
    // « C'est arrivé » rend la bulle acquise.
    const champs = geste === 'relancer' ? { dateAction: jour } : { etat: 'fait' as const };
    repondre.mutate(
      { id: c.bulleId, champs },
      {
        onSuccess: () => {
          setOuverte(null);
          onAnnonce(geste === 'relancer' ? MOTS.relancee(c.titre) : MOTS.arrivee(c.titre));
        },
        onError: () => onAnnonce(CA_N_A_PAS_MARCHE),
      }
    );
  };
  const toucher = (c: CarteDuJour, j: JourDuCalendrier) => {
    const suite = toucherDUneCarte(c, j);
    if (suite === 'gestes') setOuverte(ouverte === `${j.jour}-${c.cle}` ? null : `${j.jour}-${c.cle}`);
    else if (suite === 'onglet-journee') naviguer('/journee');
    else if (suite === 'jour') onJour(j.jour);
    else if (c.bulleId) void ouvrirLaFiche(c.bulleId);
  };

  return (
    <>
      <Periode
        titre={leLundi ? `${quantiemeEtMoisAbrege(leLundi)} – ${quantiemeEtMoisAbrege(decaler(leLundi, 6))}` : ' '}
        avant={MOTS.semaineAvant}
        apres={MOTS.semaineApres}
        onAvant={leLundi ? () => onSemaine(decaler(leLundi, -7)) : undefined}
        onApres={leLundi ? () => onSemaine(decaler(leLundi, 7)) : undefined}
      />
      <div className="agenda-pouce-semaine">
        {semaine?.map((j) => (
          <JourDeLaSemaine
            key={j.jour}
            jour={j}
            ouverte={ouverte}
            occupe={repondre.isPending}
            onOuvrir={() => onJour(j.jour)}
            onToucher={(c) => toucher(c, j)}
            onFiche={(c) => c.bulleId && void ouvrirLaFiche(c.bulleId)}
            onRelancer={(c) => repondreA(c, j.jour, 'relancer')}
            onArrive={(c) => repondreA(c, j.jour, 'arrive')}
          />
        ))}
      </div>
      {semaine && (
        <>
          <a
            className={`agenda-pouce-export${exportees.length === 0 ? ' est-eteint' : ''}`}
            href={exportees.length > 0 ? api.urlExport(exportees) : undefined}
            aria-disabled={exportees.length === 0}
          >
            {MOTS.exporter}
          </a>
          {exportees.length === 0 && <p className="agenda-pouce-note">{MOTS.exporterRien}</p>}
        </>
      )}
    </>
  );
}

/** Ce qu'une carte dit sous son titre : sa sorte quand le signe ne suffit pas, et son projet. */
function mentionDeLaCarte(c: CarteDuJour): string {
  if (c.sorte === 'journee') return MOTS.tachesDeLaJournee(c.combien, c.heure ? heureLisible(c.heure) : '');
  const sorte =
    c.sorte === 'echeance' ? MOTS.echeance : c.sorte === 'attente' ? (c.pasVenue ? MOTS.reponsePasVenue : MOTS.reponseAttendue) : null;
  return [sorte, c.projet?.titre].filter(Boolean).join(' · ');
}

/**
 * Une chose du jour dans sa carte au ton clair de son projet, son signe en tête : la semaine et le
 * jour touché du mois (ticket 06) en ont le même dessin.
 */
function CarteDeLAgenda({ carte: c, deplie, onToucher }: { carte: CarteDuJour; deplie?: boolean; onToucher: () => void }) {
  return (
    <button
      type="button"
      className={`agenda-pouce-carte agenda-pouce-carte--${c.sorte}${c.pasVenue ? ' est-pas-venue' : ''}`}
      style={c.sorte === 'journee' ? undefined : teinte(c.projet?.couleur)}
      data-carte={c.cle}
      aria-expanded={deplie}
      onClick={onToucher}
    >
      <span className="agenda-pouce-carte-titre">
        <span className="agenda-pouce-signe" aria-hidden="true">
          {SIGNES[c.sorte]}
        </span>
        {c.sorte === 'rdv' && c.heure && <span className="agenda-pouce-carte-heure">{heureLisible(c.heure)}</span>}
        <span className="agenda-pouce-carte-mot">{c.sorte === 'journee' ? MOTS.taJournee : c.titre}</span>
      </span>
      <span className="agenda-pouce-carte-mention">
        <Chevauche carte={c} />
        {mentionDeLaCarte(c)}
      </span>
    </button>
  );
}

/** « ⇄ Chevauche » : deux rendez-vous du jour l'un sur l'autre, en ambre, jamais en rouge. */
function Chevauche({ carte }: { carte: CarteDuJour }) {
  if (!carte.chevauche) return null;
  return (
    <span className="agenda-pouce-chevauche" title={MOTS.chevaucheExplique}>
      <span aria-hidden="true">⇄ </span>
      {MOTS.chevauche}
    </span>
  );
}

/**
 * Un jour de la semaine, une ligne (décision 17 de la revue) : à gauche son nom et son quantième,
 * qui ouvrent le jour ; à droite ses cartes, ou « Rien de posé ». Une réponse pas venue touchée
 * montre sous sa carte « Relancer », « C'est arrivé » et sa fiche.
 */
function JourDeLaSemaine({
  jour: j,
  ouverte,
  occupe,
  onOuvrir,
  onToucher,
  onFiche,
  onRelancer,
  onArrive,
}: {
  jour: JourDuCalendrier;
  ouverte: string | null;
  occupe: boolean;
  onOuvrir: () => void;
  onToucher: (c: CarteDuJour) => void;
  onFiche: (c: CarteDuJour) => void;
  onRelancer: (c: CarteDuJour) => void;
  onArrive: (c: CarteDuJour) => void;
}) {
  const cartes = cartesDuJour(j);
  const classes = [
    'agenda-pouce-jour',
    j.passe ? 'agenda-pouce-jour--passe' : '',
    j.estAujourdhui ? 'agenda-pouce-jour--aujourdhui' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} data-jour={j.jour}>
      <button
        type="button"
        className="agenda-pouce-date"
        onClick={onOuvrir}
        aria-label={MOTS.ouvrir(jourEnToutesLettres(j.jour))}
        aria-current={j.estAujourdhui ? 'date' : undefined}
      >
        <small>{nomCourtDuJour(j.jour)}</small>
        <b>{Number(j.jour.slice(8))}</b>
      </button>
      <div className="agenda-pouce-cartes">
        {cartes.length === 0 && (
          <button type="button" className="agenda-pouce-rien" onClick={onOuvrir}>
            {MOTS.rienDePose}
          </button>
        )}
        {cartes.map((c) => {
          const deplie = ouverte === `${j.jour}-${c.cle}`;
          return (
            <div key={c.cle} className="agenda-pouce-carte-bloc">
              <CarteDeLAgenda
                carte={c}
                deplie={toucherDUneCarte(c, j) === 'gestes' ? deplie : undefined}
                onToucher={() => onToucher(c)}
              />
              {deplie && (
                <div className="agenda-pouce-gestes" data-gestes-de={c.bulleId ?? ''}>
                  <button type="button" className="agenda-pouce-relancer" disabled={occupe} onClick={() => onRelancer(c)}>
                    {MOTS.relancer}
                  </button>
                  <button type="button" className="agenda-pouce-arrive" disabled={occupe} onClick={() => onArrive(c)}>
                    {MOTS.arrive}
                  </button>
                  <button type="button" className="agenda-pouce-vers-fiche" onClick={() => onFiche(c)}>
                    {MOTS.ouvrirLaFiche}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Le mois en grand (chantier telephone-ecrans, 02, maquette `casesDuMois()`) : chaque chose
 * posée nommée dans sa case, trois au plus puis « +N » (`leMoisEtLeJour.ts`). Toucher un jour le
 * montre dessous ; rien de touché, aujourd'hui ; « Ouvrir » l'affiche en entier.
 */
function Mois({
  mois,
  projet,
  touche,
  onMois,
  onToucher,
  onJour,
  couche = false,
}: {
  mois: string | null;
  projet: string | null;
  touche: string | null;
  onMois: (mois: string) => void;
  onToucher: (jour: string) => void;
  onJour: (jour: string) => void;
  /** Couché : le mois à gauche, le jour touché et la fiche de son premier rendez-vous à droite. */
  couche?: boolean;
}) {
  const calendrier = useQuery({ queryKey: cles.calendrier(mois), queryFn: () => api.calendrier(mois) });
  const donnees = calendrier.data;
  // Rien de touché : aujourd'hui, quand le mois le porte (le serveur dit quel jour il est).
  const leTouche = touche ?? donnees?.jours.find((j) => j.estAujourdhui && j.dansLeMois)?.jour ?? null;
  const jourTouche = donnees?.jours.find((j) => j.jour === leTouche);
  const titre = donnees ? majuscule(moisEnLettres(donnees.mois)) : ' ';
  const onAvant = donnees ? () => onMois(donnees.precedent) : undefined;
  const onApres = donnees ? () => onMois(donnees.suivant) : undefined;
  const grille = (
    <div className="agenda-pouce-grille">
      {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((n, i) => (
        <div key={i} className="agenda-pouce-nom" aria-hidden="true">
          {n}
        </div>
      ))}
      {donnees?.jours.map((j) => {
        // Hors du mois, la case reste vide, comme la maquette (`.mv.vide`).
        if (!j.dansLeMois) return <div key={j.jour} className="agenda-pouce-case agenda-pouce-case--hors" aria-hidden="true" />;
        const { barres, deplus } = barresDuJour(jourDuProjet(j, projet));
        const classes = [
          'agenda-pouce-case',
          j.passe ? 'agenda-pouce-case--passe' : '',
          j.estAujourdhui ? 'agenda-pouce-case--aujourdhui' : '',
          j.jour === leTouche ? 'est-touchee' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <button
            key={j.jour}
            type="button"
            className={classes}
            data-jour={j.jour}
            onClick={() => onToucher(j.jour)}
            aria-label={MOTS.voir(jourEnToutesLettres(j.jour))}
            aria-pressed={j.jour === leTouche}
            aria-current={j.estAujourdhui ? 'date' : undefined}
          >
            <span className="agenda-pouce-case-numero">{Number(j.jour.slice(8))}</span>
            {/* Chaque chose dit son nom, teintée par sa sorte (`.mt`, `.mt.att`, `.mt.ech`, `.mt.chv`). */}
            {barres.map((b) => (
              <span
                key={b.cle}
                className={`agenda-pouce-barre agenda-pouce-barre--${b.sorte}${b.chevauche ? ' agenda-pouce-barre--chevauche' : ''}`}
                aria-hidden="true"
              >
                {b.sorte === 'journee' ? MOTS.taJournee : b.titre}
                {b.chevauche && ' ⇄'}
              </span>
            ))}
            {deplus > 0 && (
              <span className="agenda-pouce-deplus" aria-hidden="true">
                {MOTS.deplus(deplus)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
  const leJour = jourTouche ? (
    <JourTouche jour={jourDuProjet(jourTouche, projet)} onJour={() => onJour(jourTouche.jour)} />
  ) : (
    <p className="agenda-pouce-note">{MOTS.toucherUnJour}</p>
  );

  if (couche) {
    // La fiche du premier rendez-vous du jour touché, dans l'ordre du jour (`cartesDuJour`).
    const premier = jourTouche ? apercuDuJour(jourDuProjet(jourTouche, projet)).cartes.find((c) => c.sorte === 'rdv' && c.bulleId) : undefined;
    return (
      <>
        <HautDuPouce
          couche
          titre={titre}
          apres={<Fleches avant={MOTS.moisAvant} apres={MOTS.moisApres} onAvant={onAvant} onApres={onApres} />}
        />
        <div className="agenda-couche">
          {grille}
          <div className="agenda-couche-jour">
            {leJour}
            {premier?.bulleId && <FichePosee bulleId={premier.bulleId} />}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Periode aLaMain titre={titre} avant={MOTS.moisAvant} apres={MOTS.moisApres} onAvant={onAvant} onApres={onApres} />
      {grille}
      {leJour}
    </>
  );
}

/**
 * Le jour touché du mois, montré dessous : son nom et « Ouvrir › », ses choses dans leurs cartes,
 * « Pas de journée prévue » s'il le faut. Une chose ouvre sa fiche ; une réponse pas venue, et ta
 * journée un autre jour qu'aujourd'hui, ouvrent le jour en entier.
 */
function JourTouche({ jour: j, onJour }: { jour: JourDuCalendrier; onJour: () => void }) {
  const naviguer = useNavigate();
  const ouvrirLaFiche = useAllerALaFiche();
  const { cartes, pasDeJournee, rien } = apercuDuJour(j);
  const titre = jourEnToutesLettres(j.jour);
  const toucher = (c: CarteDuJour) => {
    const suite = toucherDansLeMois(c, j);
    if (suite === 'onglet-journee') naviguer('/journee');
    else if (suite === 'jour') onJour();
    else if (c.bulleId) void ouvrirLaFiche(c.bulleId);
  };
  // Le jour touché en titre à la main (`.titre-s`), ses choses en lignes dans un paquet (`.paquet`, `.ligne`).
  return (
    <section className="agenda-pouce-touche" data-jour={j.jour} aria-label={titre}>
      <div className="agenda-pouce-touche-tete">
        <b>{majuscule(titre)}</b>
        <button type="button" className="agenda-pouce-ouvrir" aria-label={MOTS.ouvrir(titre)} onClick={onJour}>
          {MOTS.ouvrirLeJour} <span aria-hidden="true">›</span>
        </button>
      </div>
      <div className="agenda-pouce-paquet">
        {cartes.map((c) => (
          <button
            key={c.cle}
            type="button"
            className={`agenda-pouce-touche-ligne agenda-pouce-touche-ligne--${c.sorte}`}
            data-carte={c.cle}
            onClick={() => toucher(c)}
          >
            <span className="agenda-pouce-touche-t">
              {c.sorte === 'journee' ? MOTS.taJournee : c.titre}
              <small>
                {c.sorte === 'rdv' && c.heure && `${heureLisible(c.heure)} · `}
                <Chevauche carte={c} />
                {c.sorte === 'rdv' ? [MOTS.rendezVous, c.projet?.titre].filter(Boolean).join(' · ') : mentionDeLaCarte(c)}
              </small>
            </span>
          </button>
        ))}
        {pasDeJournee && <p className="agenda-pouce-touche-note">{MOTS.pasDeJourneePrevue}</p>}
        {rien && <p className="agenda-pouce-touche-note">{MOTS.rienCeJourLa}</p>}
      </div>
    </section>
  );
}

const majuscule = (texte: string) => texte.charAt(0).toUpperCase() + texte.slice(1);

const MOTS_DU_GESTE: Record<Exclude<GesteDuJour, null>, string> = {
  preparer: MOTS.preparer,
  changer: MOTS.changer,
  planifier: MOTS.planifier,
  'changer-aujourdhui': MOTS.changerAujourdhui,
};

/**
 * Un jour ouvert, en entier (décision 19 de la revue) : « Imposé » (les rendez-vous), « Ta
 * journée » (son résumé et « Changer ce jour », ou « Planifier ce jour »), « Ce qui doit arriver »
 * (⚑ les échéances, ⏸ les réponses attendues, « Relancer » pour une pas venue). Chaque ligne ouvre
 * sa fiche. Un jour passé n'a aucun geste.
 *
 * « Planifier ce jour » pose la question pour ce jour-là (ticket 01) ; « Changer ce jour » ouvre
 * la journée à modifier de l'onglet Journée (`ChangerMaJournee`, ticket 04), dont « Recommencer »
 * repose la question en gardant ce qui est fait.
 */
function JourOuvert({
  jour,
  retour,
  preparer,
  onRetour,
  onGeste,
  onFermer,
  onAnnonce,
}: {
  jour: string;
  retour: 'semaine' | 'mois';
  preparer: boolean;
  onRetour: () => void;
  onGeste: () => void;
  onFermer: (message?: string) => void;
  onAnnonce: (message: string) => void;
}) {
  const leMois = jour.slice(0, 7);
  const calendrier = useQuery({ queryKey: cles.calendrier(leMois), queryFn: () => api.calendrier(leMois) });
  const j = calendrier.data?.jours.find((x) => x.jour === jour);
  const titre = jourEnToutesLettres(jour);
  const geste = j ? gesteDuJour(j) : null;
  const taches = j?.journee?.taches ?? [];
  const rubriques = j ? rubriquesDuJour(j) : null;
  const ouvrirLaFiche = useAllerALaFiche();
  const repondre = useRepondreAuMonde();
  // « Recommencer » de « Changer ce jour » : la question, par-dessus, tant que le panneau est ouvert.
  const [recommencer, setRecommencer] = useState(false);
  useEffect(() => {
    if (!preparer) setRecommencer(false);
  }, [preparer]);

  // Les mêmes effets que « Relancer » de la semaine et d'« À vérifier » : repartir de ce jour,
  // aujourd'hui, le seul où une réponse pas venue se montre.
  const relancer = (c: CarteDuJour) => {
    if (!c.bulleId) return;
    repondre.mutate(
      { id: c.bulleId, champs: { dateAction: jour } },
      { onSuccess: () => onAnnonce(MOTS.relancee(c.titre)), onError: () => onAnnonce(CA_N_A_PAS_MARCHE) }
    );
  };

  return (
    <div className="agenda-pouce-ouvert">
      {/* Le haut du carnet (chantier telephone-habit, 02) : le retour à la semaine ou au mois dans la marge. */}
      <HautDuPouce
        titre={majuscule(titre)}
        dessus={MOTS.titre}
        retour={{ nom: retour === 'mois' ? MOTS.retourMois : MOTS.retourSemaine, onRetour }}
      />
      {j && rubriques && (
        <>
          <p className="agenda-pouce-sous">{j.estAujourdhui ? MOTS.aujourdhui : j.passe ? MOTS.passe : MOTS.aVenir}</p>

          {rubriques.impose.length > 0 && (
            <section className="agenda-pouce-rubrique-bloc" data-rubrique="impose">
              <h2 className="agenda-pouce-rubrique">{MOTS.impose}</h2>
              {rubriques.impose.map((c) => (
                <LigneDuJour
                  key={c.cle}
                  carte={c}
                  tete={<span className="agenda-pouce-ligne-heure">{c.heure ? heureLisible(c.heure) : ''}</span>}
                  mention={[MOTS.rendezVous, c.projet?.titre].filter(Boolean).join(' · ')}
                  onFiche={() => c.bulleId && void ouvrirLaFiche(c.bulleId)}
                />
              ))}
            </section>
          )}

          {(rubriques.journee || geste) && (
            <section className="agenda-pouce-rubrique-bloc" data-rubrique="journee">
              <h2 className="agenda-pouce-rubrique">{MOTS.taJournee}</h2>
              {rubriques.journee && (
                <div className="agenda-pouce-journee">
                  <p className="agenda-pouce-journee-tete">
                    <span aria-hidden="true">{SIGNES.journee} </span>
                    {mentionDeLaCarte(rubriques.journee)}
                  </p>
                  {taches.map((t) => (
                    <button
                      key={t.bulleId}
                      type="button"
                      className={`agenda-pouce-tache${t.etat === 'fait' ? ' est-faite' : ''}`}
                      data-tache={t.bulleId}
                      onClick={() => void ouvrirLaFiche(t.bulleId)}
                    >
                      <span className="agenda-pouce-tache-heure">{heureLisible(t.heure)}</span>
                      <span className="agenda-pouce-tache-texte">
                        <span className="agenda-pouce-tache-titre">{t.titre}</span>
                        <span className="agenda-pouce-ligne-mention">
                          {[t.dossier?.titre, dureeLisible(t.dureeMin), t.etat === 'fait' ? MOTS.faite : null].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {geste && (
                <button
                  type="button"
                  className={`agenda-pouce-geste${rubriques.journee ? ' agenda-pouce-geste--doux' : ''}`}
                  data-geste={geste}
                  onClick={onGeste}
                >
                  {MOTS_DU_GESTE[geste]}
                  {rubriques.journee && <span aria-hidden="true"> ›</span>}
                </button>
              )}
            </section>
          )}

          {rubriques.doitArriver.length > 0 && (
            <section className="agenda-pouce-rubrique-bloc" data-rubrique="doit-arriver">
              <h2 className="agenda-pouce-rubrique">{MOTS.ceQuiDoitArriver}</h2>
              {rubriques.doitArriver.map((c) => (
                <LigneDuJour
                  key={c.cle}
                  carte={c}
                  tete={
                    <span className="agenda-pouce-ligne-signe" aria-hidden="true">
                      {SIGNES[c.sorte]}
                    </span>
                  }
                  mention={mentionDeLaCarte(c)}
                  onFiche={() => c.bulleId && void ouvrirLaFiche(c.bulleId)}
                  geste={
                    c.pasVenue ? (
                      <button
                        type="button"
                        className="agenda-pouce-relancer"
                        aria-label={MOTS.relancerPour(c.titre)}
                        disabled={repondre.isPending}
                        onClick={() => relancer(c)}
                      >
                        {MOTS.relancer}
                      </button>
                    ) : undefined
                  }
                />
              ))}
            </section>
          )}

          {j.passe && rubriques.impose.length === 0 && !rubriques.journee && rubriques.doitArriver.length === 0 && (
            <p className="agenda-pouce-sous">{MOTS.rienCeJourLa}</p>
          )}

          {/*
            « Planifier ce jour », un jour sans journée : la même question que l'onglet Journée,
            pour ce jour-là (ticket 01). « Changer ce jour » : la journée à modifier (ticket 04),
            et « Recommencer » repose la question en gardant ce qui est fait. Un jour passé n'a pas
            de geste, même demandé par l'adresse.
          */}
          {preparer && geste && ouvreLaQuestion(geste) && (
            <PlanifierUnJour key={jour} jour={jour} plein retour={AGENDA} onFermer={onFermer} />
          )}
          {preparer && geste && !ouvreLaQuestion(geste) && !recommencer && (
            <ChangerMaJournee
              key={jour}
              jour={jour}
              retour={AGENDA}
              titre={MOTS_DU_GESTE[geste]}
              vide={j.estAujourdhui ? RIEN_DE_PREVU_AUJOURDHUI : RIEN_DE_PREVU_CE_JOUR}
              onRetour={() => onFermer()}
              onRecommencer={() => setRecommencer(true)}
              onFermer={onFermer}
            />
          )}
          {preparer && geste && !ouvreLaQuestion(geste) && recommencer && (
            <PlanifierUnJour
              key={`${jour}-recommencer`}
              jour={jour}
              plein
              retour={AGENDA}
              phrase={ON_RECOMMENCE}
              faites={taches.filter((t) => t.etat === 'fait')}
              onFermer={onFermer}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Une ligne du jour ouvert : sa tête (l'heure, ou le signe), son titre et sa mention ; toucher
 * la ligne ouvre sa fiche. Un geste à droite (« Relancer ») prend la place du ›.
 */
function LigneDuJour({
  carte: c,
  tete,
  mention,
  geste,
  onFiche,
}: {
  carte: CarteDuJour;
  tete: ReactNode;
  mention: string;
  geste?: ReactNode;
  onFiche: () => void;
}) {
  return (
    <div className={`agenda-pouce-ligne agenda-pouce-ligne--${c.sorte}`} data-carte={c.cle}>
      <button type="button" className="agenda-pouce-ligne-fiche" onClick={onFiche}>
        {tete}
        <span className="agenda-pouce-ligne-texte">
          <span className="agenda-pouce-ligne-titre">{c.titre}</span>
          <span className="agenda-pouce-ligne-mention">
            <Chevauche carte={c} />
            {mention}
          </span>
        </span>
        {!geste && (
          <span className="agenda-pouce-ligne-vers" aria-hidden="true">
            ›
          </span>
        )}
      </button>
      {geste}
    </div>
  );
}
